import { NavLink, Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { loginRequest } from '../hooks/msalConfig';
import { EnvironmentBadge } from './EnvironmentBadge';

const navLinks = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/contact', label: 'Contact' },
];

export function SiteHeader({ isAuthenticated }: { isAuthenticated: boolean }) {
  const { instance } = useMsal();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSignIn = () => {
    instance.loginRedirect(loginRequest).catch((err) => console.error('Login failed:', err));
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm transition-colors ${
      isActive ? 'text-brand-navy font-semibold' : 'text-brand-charcoal hover:text-brand-navy'
    }`;

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-brand-mute/40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="h-20 flex items-center justify-between gap-4">
          {/* BR-CP-HOME-004 — the public pages carry the environment badge under the logo (Dev / Staging only). */}
          <div className="flex flex-col items-start gap-1 shrink-0">
            <Link to="/" className="flex items-center gap-3 shrink-0">
              <img
                src="/brand/ba-logo-original.jpg"
                alt="B&A Accounting Group"
                className="h-12 w-auto"
                width={222}
                height={120}
              />
              <span className="hidden md:flex flex-col leading-tight">
                <span className="text-base font-bold text-brand-navy tracking-tight">B&amp;A Accounting Group</span>
                <span className="text-xs text-brand-slate">Toowoomba accountants since 1981</span>
              </span>
            </Link>
            <EnvironmentBadge />
          </div>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={linkClass}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <Link
                to="/portal"
                className="px-4 py-2 rounded-md bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy-deep transition-colors"
              >
                My Portal
              </Link>
            ) : (
              <button
                type="button"
                onClick={handleSignIn}
                className="px-4 py-2 rounded-md bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy-deep transition-colors"
              >
                Client Portal
              </button>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            className="lg:hidden text-brand-charcoal"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden pb-4 border-t border-brand-mute/40 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setMobileOpen(false)}
                className={linkClass}
              >
                {l.label}
              </NavLink>
            ))}
            {isAuthenticated ? (
              <Link
                to="/portal"
                onClick={() => setMobileOpen(false)}
                className="mt-2 px-4 py-2 rounded-md bg-brand-navy text-white text-sm font-semibold text-center"
              >
                My Portal
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  handleSignIn();
                }}
                className="mt-2 px-4 py-2 rounded-md bg-brand-navy text-white text-sm font-semibold"
              >
                Client Portal
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
